import wAxios from '@/plugins/w/axios/models/axios'
import { defineStore } from 'pinia'
import '@/plugins/w/auth/utils/refreshExpired'
import axios, { AxiosResponse } from 'axios'
import { User } from '../types'

interface State {
	authorization: Object | null
	status: string
	_token: string
	user: User
	currentAccess: Object | null
	currentProvider: Object | null

	lastAccess: Object | null
	lastProvider: Object | null
	lastOcAccess: Object | null
	currentOcAccess: Object | null
}

export const useAuthStore = defineStore('auth', {
	state: (): State => ({
		authorization: null,
		status: '',
		_token: localStorage.getItem('mdx_kz_user_token') || '',
		user: JSON.parse(localStorage.getItem('mdx_kz_user_user') || '{}'),
		currentAccess: localStorage.getItem('mdx_kz_current_access') ? JSON.parse(localStorage.getItem('mdx_kz_current_access') || '{}') : null,
		currentProvider: localStorage.getItem('mdx_kz_current_provider') ? JSON.parse(localStorage.getItem('mdx_kz_current_provider') || '{}') : null,
		currentOcAccess: localStorage.getItem('mdx_kz_current_oc_access') ? JSON.parse(localStorage.getItem('mdx_kz_current_oc_access') || '{}') : null,

		lastAccess: null,
		lastProvider: null,
		lastOcAccess: null
	}),
	actions: {
		login(credentials: any) {
			return new Promise((resolve, reject) => {
				this.status = 'loading'

				wAxios
					.post('v1/auth/login', credentials)
					.then((response: any) => {
						this.authorization = {
							lis_token: response.lis_token,
							confirm_data: response.confirm_data || null,
							login: credentials.login
						}

						resolve(response)
					})
					.catch((error) => {
						this.authorization = null
						reject(error)
					})
			})
		},

		loginOc(credentials: { login: string; password: string }): Promise<AxiosResponse> {
			return new Promise((resolve, reject) => {
				this.status = 'loading'

				axios
					.post('v1/auth/oc/login', credentials)
					.then((response: AxiosResponse) => {
						const { data, status } = response

						if (status === 200) {
							if (data.token) {
								this.setToken(data.token)
							}

							if (data.user) {
								this.setUser(data.user)
							}

							this.lastOcAccess = JSON.parse(localStorage.getItem(`mdx_kz_last_oc_access_${data.user.id}`) || '{}')

							resolve(response)
						} else if (status === 206) {
							this.authorization = {
								lis_token: data.lis_token,
								confirm_data: data.confirm_data || null,
								login: credentials.login
							}

							resolve(response)
						} else {
							reject(response)
						}
					})
					.catch((error) => {
						this.authorization = null
						reject(error)
					})
			})
		},

		loginGrid(credentials: any) {
			return new Promise((resolve, reject) => {
				// this.status = 'loading'

				wAxios
					.post('v1/auth/login/confirm', {
						...credentials,
						lis_token: this.authorization.lis_token,
						login: this.authorization.login
					})
					.then((response: any) => {
						const token = response.token
						const user = response.user

						this.status = 'success'

						if (token) {
							this.setToken(token)
						}

						if (user) {
							this.setUser(user)
							this.lastAccess = JSON.parse(localStorage.getItem(`mdx_kz_last_access_${user.id}`) || '{}')
							this.lastProvider = JSON.parse(localStorage.getItem(`mdx_kz_last_provider_${user.id}`) || '{}')
						}

						resolve(response)
					})
					.catch((error) => {
						if (error.response.data.error == 'Platnosť tokenu vypršala.') {
							console.log('Token expired')
							this.logout()
							reject('Nepodarilo sa overiť kód. Skúste sa prihlásiť znova.')
						} else {
							reject(error)
						}
					})
			})
		},

		async logout(refresh = true) {
			if (!this._token) return

			// Clear local session first so 401 from invalidate (or a redirect to login)
			// cannot bounce the user back into an authenticated route.
			const token = this._token
			this.status = ''
			this.setUser()
			this.setToken()

			try {
				await axios.post(
					'v1/auth/invalidate',
					{},
					{ headers: { Authorization: `Bearer ${token}` } }
				)
			} catch (error) {
				console.error('Failed to invalidate auth token on logout', error)
			}

			if (refresh) {
				window.location.reload()
			}
		},
		forgotPassword(credentials: any) {
			return new Promise((resolve, reject) => {
				wAxios
					.post_auth_data('v1/auth/reset', credentials)
					.then((response) => resolve(response))
					.catch((error) => reject(error))
			})
		},
		unblockAccount(credentials: any) {
			return new Promise((resolve, reject) => {
				wAxios
					.post_auth_data('v1/auth/unblock', credentials)
					.then((response) => resolve(response))
					.catch((error) => reject(error))
			})
		},
		async refreshToken() {
			return new Promise((resolve, reject) => {
				wAxios
					.post_auth_data('v1/auth/refresh')
					.then((response: any) => {
						const token = response.token
						const user = response.user

						this.status = 'success'
						if (token) {
							this.setToken(token)
						}

						if (user) {
							this.user = user
							localStorage.setItem('mdx_kz_user_user', JSON.stringify(user))
						}

						resolve(token)
					})
					.catch((error) => {
						this.logout()
						reject(error)
					})
			})
		},
		async userInfo() {
			return new Promise((resolve, reject) => {
				wAxios
					.get_auth_data('v1/auth/info')
					.then((response: any) => {
						const user = response.user

						this.status = 'success'

						if (user) {
							this.user = user
							localStorage.setItem('mdx_kz_user_user', JSON.stringify(user))
						}

						resolve(user)
					})
					.catch((error) => {
						reject(error)
					})
			})
		},
		updateUserInfo(data: any) {
			return new Promise((resolve, reject) => {
				wAxios
					.post_auth_data('v1/auth/info', data)
					.then((response: any) => {
						const user = response.user

						this.status = 'success'

						if (user) {
							this.user = user
							localStorage.setItem('mdx_kz_user_user', JSON.stringify(user))
						}

						resolve(user)
					})
					.catch((error) => {
						reject(error)
					})
			})
		},

		setLzAccess({ provider, access }) {
			return new Promise((resolve, reject) => {
				this.currentAccess = access
				this.currentProvider = provider

				localStorage.setItem('mdx_kz_current_access', JSON.stringify(access))
				localStorage.setItem('mdx_kz_current_provider', JSON.stringify(provider))

				if (this.user.id) {
					localStorage.setItem(`mdx_kz_last_access_${this.user.id}`, JSON.stringify(access))
					localStorage.setItem(`mdx_kz_last_provider_${this.user.id}`, JSON.stringify(provider))
				}

				// Fix for expanded results, every time current access has to be set
				try {
					const accessIds = JSON.parse(localStorage.getItem(`results_accesses_user_${this.user.id}`) || '[]')

					if (!accessIds || accessIds.length === 1) {
						localStorage.setItem(`results_accesses_user_${this.user.id}`, JSON.stringify([access.id]))
					} else if (!accessIds.includes(access.id)) {
						accessIds.push(access.id)
						localStorage.setItem(`results_accesses_user_${this.user.id}`, JSON.stringify(accessIds))
					}
				} catch (error) {
					localStorage.setItem(`results_accesses_user_${this.user.id}`, JSON.stringify([access.id]))
				}
			})
		},

		setOcAccess({ access }) {
			this.currentOcAccess = access

			localStorage.setItem(`mdx_kz_current_oc_access`, JSON.stringify(access))

			if (this.user.id) {
				localStorage.setItem(`mdx_kz_last_oc_access_${this.user.id}`, JSON.stringify(access))
			}
		},

		setToken(token?: string) {
			if (!token) {
				this._token = null
				localStorage.removeItem('mdx_kz_user_token')
			} else {
				this._token = token
				localStorage.setItem('mdx_kz_user_token', token)
			}
		},

		setUser(user?: any) {
			if (!user) {
				localStorage.removeItem('mdx_kz_user_user')
				localStorage.removeItem('mdx_kz_current_access')
				localStorage.removeItem('mdx_kz_current_oc_access')
				localStorage.removeItem('mdx_kz_current_provider')
				this.user = null
			} else {
				this.user = user
				localStorage.setItem('mdx_kz_user_user', JSON.stringify(user))
			}
		}
	},
	getters: {
		isLoggedIn: (state) => {
			return !!state._token
		},
		getUser: (state: any) => {
			return state.user
		},
		getUserType: (state: any): 'OC' | 'KZ' => {
			return state.user?.type
		},
		getToken: (state: any) => {
			return state._token
		},
		getAuthorization() {
			return this.authorization
		},
		getOcAccesses() {
			return this.user?.oc?.accesses ?? []
		},
		getProviders() {
			if (!this.user) return null
			return this.user.providers
		},

		getAmbulances() {
			return this.user?.ambulances ?? []
		},

		getCurrentAccess() {
			if (!this.currentAccess) return null

			return this.currentAccess
		},

		getCurrentProvider() {
			if (!this.currentProvider) return null

			return this.currentProvider
		},

		getCurrentOcAccess() {
			if (!this.currentOcAccess) return null

			return this.currentOcAccess
		},

		getLastAccess() {
			if (!this.lastAccess) return null

			return this.lastAccess
		},

		getLastOcAccess() {
			if (!this.lastOcAccess) return null

			return this.lastOcAccess
		},

		getLastProvider() {
			if (!this.lastProvider) return null

			return this.lastProvider
		},

		getUserFullName() {
			if (!this.user) return null

			return `${this.user.degree?.before ? this.user.degree.before + ' ' : ''}${this.user.name} ${this.user.surname}${this.user.degree?.after ? ', ' + this.user.degree.after : ''}`
		}
	}
})
